import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

import { useUser, ROLES } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import { useSelectedOrganization } from '../../hooks/useSelectedOrganization';
import { useOrganizationLocations } from '../../hooks/useOrganizationData';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './Settings.css';

const UserEditor = ({ userOrganizationId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin, isSuperAdmin, userInfo, isLoading: isUserLoading, refreshUserData } = useUser();
  const navigate = useNavigate();
  const { userId } = useParams();
  const activeOrganizationId = useSelectedOrganization(userOrganizationId);
  const notification = useNotification();

  const isEditMode = !!userId;

  const { data: locations = [] } = useOrganizationLocations(activeOrganizationId);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Form fields for invite mode
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstname: '',
    lastname: '',
    role: ROLES.AGENT,
    location_ids: []
  });

  // Form fields for edit mode
  const [selectedRole, setSelectedRole] = useState(ROLES.AGENT);
  const [selectedLocations, setSelectedLocations] = useState([]);

  useEffect(() => {
    if (!isUserLoading && !isAdmin()) {
      notification.error('You do not have permission to access this page');
      navigate('/dashboard');
    }
  }, [isAdmin, isUserLoading, navigate, notification]);

  const fetchUser = async () => {
    if (!userId) return;

    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const user = response.data;
      setEditingUser(user);
      setSelectedRole(user.role || ROLES.AGENT);
      setSelectedLocations(
        user.assigned_locations?.map(loc => loc._id || loc.id) || []
      );
    } catch (error) {
      console.error('Error fetching user:', error);
      notification.error('Failed to load user');
      navigate('/settings/users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode) {
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, getAccessTokenSilently]);

  const handleCancel = () => {
    navigate('/settings/users');
  };

  const canEditUser = () => {
    if (!editingUser) return false;
    const targetId = editingUser.id || editingUser._id;
    if (targetId === userInfo?.id) return false;
    if (isSuperAdmin()) return true;
    if (isAdmin() && editingUser.role === ROLES.AGENT) return true;
    return false;
  };

  const canDeleteUser = () => {
    if (!editingUser) return false;
    const targetId = editingUser.id || editingUser._id;
    if (targetId === userInfo?.id) return false;
    if (isSuperAdmin() && editingUser.role !== ROLES.SUPER_ADMIN) return true;
    if (isAdmin() && editingUser.role === ROLES.AGENT) return true;
    return false;
  };

  const getAvailableRoles = () => {
    if (isSuperAdmin()) {
      return [
        { value: ROLES.SUPER_ADMIN, label: 'Super Admin' },
        { value: ROLES.ADMIN, label: 'Admin' },
        { value: ROLES.AGENT, label: 'Agent' }
      ];
    }
    return [
      { value: ROLES.ADMIN, label: 'Admin' },
      { value: ROLES.AGENT, label: 'Agent' }
    ];
  };

  const handleInviteSubmit = async (e) => {
    e?.preventDefault();

    if (!inviteForm.email) {
      notification.error('Email is required');
      return;
    }

    try {
      setIsSaving(true);
      const token = await getAccessTokenSilently();

      await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/invite`,
        {
          email: inviteForm.email,
          firstname: inviteForm.firstname,
          lastname: inviteForm.lastname,
          role: inviteForm.role,
          location_ids: inviteForm.role === ROLES.AGENT ? inviteForm.location_ids : []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      notification.success('Invitation sent successfully');
      navigate('/settings/users');
    } catch (error) {
      console.error('Error inviting user:', error);
      notification.error(error.response?.data?.error || 'Failed to send invitation');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e?.preventDefault();

    if (!editingUser) return;

    try {
      setIsSaving(true);
      const token = await getAccessTokenSilently();

      // Update role
      await axios.patch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/${editingUser.id || editingUser._id}/role`,
        { role: selectedRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update location assignments (only for agents)
      if (selectedRole === ROLES.AGENT) {
        await axios.post(
          `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/${editingUser.id || editingUser._id}/locations`,
          { location_ids: selectedLocations },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      notification.success('User updated successfully');

      // Refresh current user data if editing self
      if ((editingUser.id || editingUser._id) === userInfo?.id) {
        refreshUserData();
      }

      navigate('/settings/users');
    } catch (error) {
      console.error('Error updating user:', error);
      notification.error(error.response?.data?.error || 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);

    if (!editingUser) return;

    try {
      const token = await getAccessTokenSilently();
      await axios.delete(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/${editingUser.id || editingUser._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success('User deleted successfully');
      navigate('/settings/users');
    } catch (error) {
      console.error('Error deleting user:', error);
      notification.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleStatusToggleClick = () => {
    setStatusDialogOpen(true);
  };

  const handleStatusToggleConfirm = async () => {
    setStatusDialogOpen(false);

    if (!editingUser) return;

    const userId = editingUser.id || editingUser._id;
    const newStatus = editingUser.status === 'active' ? 'inactive' : 'active';
    const endpoint = newStatus === 'inactive' ? 'deactivate' : 'reactivate';

    try {
      setIsTogglingStatus(true);
      const token = await getAccessTokenSilently();
      await axios.patch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/${userId}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      notification.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);

      // Update local state
      setEditingUser({ ...editingUser, status: newStatus });
    } catch (error) {
      console.error('Error toggling user status:', error);
      notification.error(error.response?.data?.error || 'Failed to update user status');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleStatusToggleCancel = () => {
    setStatusDialogOpen(false);
  };

  if (isUserLoading || isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page-header">
          <div className="settings-page-header-left">
            <button className="settings-back-btn" onClick={handleCancel}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6" />
              </svg>
              Back
            </button>
            <h1 className="settings-page-title">
              {isEditMode ? 'Edit User' : 'Invite New User'}
            </h1>
            <p className="settings-page-subtitle">Loading...</p>
          </div>
        </div>
        <div className="settings-page-content">
          <div className="settings-card">
            <div className="settings-loading">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  // Invite mode
  if (!isEditMode) {
    return (
      <div className="settings-page">
        <div className="settings-page-header">
          <div className="settings-page-header-left">
            <button className="settings-back-btn" onClick={handleCancel}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6" />
              </svg>
              Back
            </button>
            <h1 className="settings-page-title">Invite New User</h1>
            <p className="settings-page-subtitle">Send an invitation to add a new team member</p>
          </div>
        </div>

        <div className="settings-page-content">
          <form onSubmit={handleInviteSubmit} id="invite-form">
            <div className="settings-card">
              <div className="settings-card-header">
                <h3 className="settings-card-title">User Information</h3>
              </div>
              <div className="settings-card-body">
                <div className="settings-form-grid">
                  <div className="settings-form-group full-width">
                    <label className="settings-form-label">
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      className="settings-form-input"
                      placeholder="user@example.com"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="settings-form-group">
                    <label className="settings-form-label">First Name</label>
                    <input
                      type="text"
                      className="settings-form-input"
                      placeholder="First Name"
                      value={inviteForm.firstname}
                      onChange={(e) => setInviteForm({ ...inviteForm, firstname: e.target.value })}
                    />
                  </div>

                  <div className="settings-form-group">
                    <label className="settings-form-label">Last Name</label>
                    <input
                      type="text"
                      className="settings-form-input"
                      placeholder="Last Name"
                      value={inviteForm.lastname}
                      onChange={(e) => setInviteForm({ ...inviteForm, lastname: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-header">
                <h3 className="settings-card-title">Role & Permissions</h3>
              </div>
              <div className="settings-card-body">
                <div className="settings-form-grid">
                  <div className="settings-form-group full-width">
                    <label className="settings-form-label">
                      Role <span className="required">*</span>
                    </label>
                    <select
                      className="settings-form-select"
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    >
                      {getAvailableRoles().map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                    <p className="settings-form-hint">
                      {inviteForm.role === ROLES.ADMIN && 'Admins have full access to all locations within their organization.'}
                      {inviteForm.role === ROLES.SUPER_ADMIN && 'Super Admins have full platform access across all organizations.'}
                      {inviteForm.role === ROLES.AGENT && 'Agents have access to assigned locations only.'}
                    </p>
                  </div>

                  {inviteForm.role === ROLES.AGENT && (
                    <div className="settings-form-group full-width">
                      <label className="settings-form-label">Assign to Locations</label>
                      <p className="settings-form-hint" style={{ marginTop: 0, marginBottom: '12px' }}>
                        Select which locations this agent will have access to
                      </p>
                      <div className="settings-checkbox-list">
                        {locations.map(location => {
                          const locId = location._id || location.id;
                          return (
                            <label key={locId} className="settings-checkbox-item">
                              <input
                                type="checkbox"
                                checked={inviteForm.location_ids.includes(locId)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setInviteForm({
                                      ...inviteForm,
                                      location_ids: [...inviteForm.location_ids, locId]
                                    });
                                  } else {
                                    setInviteForm({
                                      ...inviteForm,
                                      location_ids: inviteForm.location_ids.filter(id => id !== locId)
                                    });
                                  }
                                }}
                              />
                              <span className="settings-checkbox-label">{location.store_name}</span>
                            </label>
                          );
                        })}
                        {locations.length === 0 && (
                          <p className="settings-no-locations">No locations available. Create locations first.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="settings-action-bar">
          <button
            type="button"
            className="settings-btn secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="invite-form"
            className="settings-btn primary"
            disabled={isSaving || !inviteForm.email}
          >
            {isSaving ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      </div>
    );
  }

  // Edit mode
  const userName = editingUser
    ? `${editingUser.firstname || editingUser.given_name || ''} ${editingUser.lastname || editingUser.family_name || ''}`.trim()
    : '';

  const isUserActive = editingUser?.status === 'active';
  const statusAction = isUserActive ? 'Deactivate' : 'Activate';

  return (
    <div className="settings-page">
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete User"
        description={`Are you sure you want to delete "${userName}"? This will permanently remove them from the system and Auth0. This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ConfirmDialog
        open={statusDialogOpen}
        title={`${statusAction} User`}
        description={isUserActive
          ? `Are you sure you want to deactivate "${userName}"? They will no longer be able to access the system.`
          : `Are you sure you want to activate "${userName}"? They will regain access to the system.`
        }
        onConfirm={handleStatusToggleConfirm}
        onCancel={handleStatusToggleCancel}
        confirmText={statusAction}
        cancelText="Cancel"
      />

      <div className="settings-page-header">
        <div className="settings-page-header-left">
          <button className="settings-back-btn" onClick={handleCancel}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15,18 9,12 15,6" />
            </svg>
            Back
          </button>
          <h1 className="settings-page-title">Edit User</h1>
          <p className="settings-page-subtitle">Update user role and permissions for {userName}</p>
        </div>
      </div>

      <div className="settings-page-content">
        <form onSubmit={handleEditSubmit} id="edit-form">
          <div className="settings-card">
            <div className="settings-card-header">
              <h3 className="settings-card-title">User Information</h3>
            </div>
            <div className="settings-card-body">
              <div className="settings-form-grid">
                <div className="settings-form-group">
                  <label className="settings-form-label">Name</label>
                  <input
                    type="text"
                    className="settings-form-input"
                    value={userName}
                    disabled
                  />
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-label">Email</label>
                  <input
                    type="email"
                    className="settings-form-input"
                    value={editingUser?.email || ''}
                    disabled
                  />
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-label">Status</label>
                  <div className="settings-status-display">
                    <span className={`settings-status-badge ${isUserActive ? 'active' : 'inactive'}`}>
                      {isUserActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="settings-card">
            <div className="settings-card-header">
              <h3 className="settings-card-title">Role & Permissions</h3>
            </div>
            <div className="settings-card-body">
              <div className="settings-form-grid">
                <div className="settings-form-group full-width">
                  <label className="settings-form-label">
                    Role <span className="required">*</span>
                  </label>
                  <select
                    className="settings-form-select"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    disabled={!canEditUser()}
                  >
                    {getAvailableRoles().map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  <p className="settings-form-hint">
                    {selectedRole === ROLES.ADMIN && 'Admins have full access to all locations within their organization.'}
                    {selectedRole === ROLES.SUPER_ADMIN && 'Super Admins have full platform access across all organizations.'}
                    {selectedRole === ROLES.AGENT && 'Agents have access to assigned locations only.'}
                  </p>
                </div>

                {selectedRole === ROLES.AGENT && (
                  <div className="settings-form-group full-width">
                    <label className="settings-form-label">Assigned Locations</label>
                    <p className="settings-form-hint" style={{ marginTop: 0, marginBottom: '12px' }}>
                      Select which locations this agent will have access to
                    </p>
                    <div className="settings-checkbox-list">
                      {locations.map(location => {
                        const locId = location._id || location.id;
                        return (
                          <label key={locId} className="settings-checkbox-item">
                            <input
                              type="checkbox"
                              checked={selectedLocations.includes(locId)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedLocations([...selectedLocations, locId]);
                                } else {
                                  setSelectedLocations(selectedLocations.filter(id => id !== locId));
                                }
                              }}
                              disabled={!canEditUser()}
                            />
                            <span className="settings-checkbox-label">{location.store_name}</span>
                          </label>
                        );
                      })}
                      {locations.length === 0 && (
                        <p className="settings-no-locations">No locations available. Create locations first.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="settings-action-bar">
        <button
          type="button"
          className="settings-btn secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
        {canEditUser() && (
          <>
            <button
              type="submit"
              form="edit-form"
              className="settings-btn primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Update'}
            </button>
            <button
              type="button"
              className={`settings-btn ${isUserActive ? 'warning' : 'success'}`}
              onClick={handleStatusToggleClick}
              disabled={isTogglingStatus}
            >
              {isTogglingStatus ? 'Processing...' : statusAction}
            </button>
          </>
        )}
        {canDeleteUser() && (
          <button
            type="button"
            className="settings-btn danger"
            onClick={handleDeleteClick}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default UserEditor;
